import { inject, Injectable } from '@angular/core';
import { ProductsStore } from './products/products.store';
import { DashboardStore } from './dashboard/dashboard.store';
import dayjs from 'dayjs';
import { FaqStore } from './faq/faq.store';
import { EFaqPageCode, IImageUploadProps, IPageRequest } from 'src/models/common.model';
import { AppStore } from './app/app.store';
import { TransactionsStore } from './transactions/transactions.store';
import { ITransaction, ITransactionsQueryParams } from 'src/models/transactions.model';
import { AccountStore } from './account/account.store';
import { IPortfolioDetailChartParams, IEntryFeeCalcInput, INewInvestmentProgress, IModelPortfolioChartInput, IModelStrategyChartInput, INewPortfolioInput, INewStrategyInput, IBackcastingChartInput, IEditInvestmentProgress, IUpdatePortfolioV2Input, IUpdateStrategyInput, IModelPortfolioOut, IUpdateEfektikaStrategyInput, EWithdrawalPortfolioType } from 'src/models/products.model';
import { INotification } from 'src/models/app.model';
import { ELoadingEntity, IErrorProperty } from 'src/models/generic-store.model';
import { BlogStore } from './blog/blog.store';
import { BankAccountsStore } from './bank-accounts/bank-accounts.store';
import { IDashboardChartInput } from 'src/models/dashboard.model';
import { IInstrumentChartInput } from 'src/models/charts.model';

@Injectable({ providedIn: 'root' })
export class DataService {

  // Feature stores
  private productsStore = inject(ProductsStore);
  private dashboardStore = inject(DashboardStore);
  private faqStore = inject(FaqStore);
  private appStore = inject(AppStore);
  private transactionsStore = inject(TransactionsStore);
  private accountStore = inject(AccountStore);
  private blogStore = inject(BlogStore);
  private bankAccountsStore = inject(BankAccountsStore);

  //!-------------- APP ---------------------------------------------------------------
  // State signals
  public hotlineAvailability = this.appStore.hotlineAvailability;
  public collaboratorInfo = this.appStore.collaboratorInfo;
  public collaboratorPhoto = this.appStore.collaboratorPhoto;
  public appLoadingProps = this.appStore.loadingProps;
  public unreadItemsSumCount = this.appStore.unreadItemsSumCount;
  public unreadMessagesCount = this.appStore.unreadMessagesCount;
  public unreadNotificationsCount = this.appStore.unreadNotificationsCount;
  public selectedNotification = this.appStore.selectedNotification;
  public hideFinancialValues = this.appStore.hideFinancialValues;

  public appPropsIsLoading(loadingEntity: ELoadingEntity): boolean {
    return this.appStore.isLoading(loadingEntity);
  }

  public toggleHideFinancialValues(): void {
    this.appStore.toggleHideFinancialValues();
  }

  public resetHideFinancialValues(): void {
    this.appStore.resetHideFinancialValues();
  }

  public getHotlineAvailability(): void {
    this.appStore.getHotlineAvailability();
  }

  public getCollaboratorInfo(): void {
    this.appStore.getCollaboratorInfo();
  }

  public getCollaboratorPhoto(): void {
    this.appStore.getCollaboratorPhoto();
  }

  public resetCollaboratorState(): void {
    this.appStore.resetCollaboratorState();
  }

  public appHasErrored(loadingEntity: ELoadingEntity): boolean {
    return this.appStore.hasErrored(loadingEntity);
  }

  public appGetError(loadingEntity: ELoadingEntity): IErrorProperty | null {
    return this.appStore.getError(loadingEntity);
  }

  public getNewNotificationsCount(): void {
    this.appStore.getNewNotificationsCount();
  }

  public clearSelectedNotification(): void {
    this.appStore.clearSelectedNotification();
  }

  public setSelectedNotification(notification: INotification): void {
    this.appStore.setSelectedNotification(notification);
  }

  //!-------------- PRODUCTS ----------------------------------------------------------

  public portfolios = this.productsStore.portfolios;
  public allInvestments = this.productsStore.allInvestments;
  public thematicStrategies = this.productsStore.thematicStrategies;
  public draftInvestments = this.productsStore.draftInvestments;
  public selectedPortfolio = this.productsStore.selectedPortfolio;
  public selectedDraftPortfolio = this.productsStore.selectedDraftPortfolio;
  public portfoliosTotal = this.productsStore.portfoliosTotal;
  public portfolioFeeConfigs = this.productsStore.portfolioFeeConfigs;
  public portfolioHistoryChartData = this.productsStore.portfolioHistoryChartData;
  public modelPortfolio = this.productsStore.modelPortfolio;
  public selectedModelStrategy = this.productsStore.selectedModelStrategy;
  public modelPortfolioByRiskScore = this.productsStore.modelPortfolioByRiskScore;
  public investmentFutureDevelopmentChart = this.productsStore.investmentFutureDevelopmentChart;
  public investmentBackcastingChart = this.productsStore.backcastingChart;
  public instrumentChart = this.productsStore.instrumentChart;
  public instrumentRatios = this.productsStore.instrumentRatios;
  public entryFeeToDisplay = this.productsStore.entryFeeToDisplay;
  public newInvestmentProgress = this.productsStore.newInvestmentProgress;
  public editInvestmentProgress = this.productsStore.editInvestmentProgress;
  public availableInstruments = this.productsStore.availableInstruments;
  public instrumentRegions = this.productsStore.instrumentRegions;
  public publicInstruments = this.productsStore.publicInstruments;
  public publicInstrumentRegions = this.productsStore.publicInstrumentRegions;
  public cashInstruments = this.productsStore.cashInstruments;
  public userCashInstrument = this.productsStore.userCashInstrument;
  public strategyLimits = this.productsStore.strategyLimits;
  public selectedPortfolioInstrumentRemovals = this.productsStore.selectedPortfolioInstrumentRemovals;
  public productsLoadingProps = this.productsStore.loadingProps;
  public draftsListLoadingProps = this.productsStore.loadingProps;
  public depositInfo = this.productsStore.depositInfo;

  public productsIsLoading(loadingEntity: ELoadingEntity): boolean {
    return this.productsStore.isLoading(loadingEntity);
  }

  public productsHasErrored(loadingEntity: ELoadingEntity): boolean {
    return this.productsStore.hasErrored(loadingEntity);
  }

  public productsClearError(loadingEntity: ELoadingEntity): void {
    this.productsStore.clearError(loadingEntity);
  }

  public getPortfolioOverviews(params: IPageRequest): void {
    this.productsStore.getPortfolioOverviews(params);
  }

  public getWithdrawalPortfolios(params: IPageRequest, type: EWithdrawalPortfolioType): void {
    this.productsStore.getWithdrawalPortfolios({ params, type });
  }

  public getAllInvestments(): void {
    this.productsStore.getAllInvestments();
  }

  public productsGetError(loadingEntity: ELoadingEntity): IErrorProperty | null {
    return this.productsStore.getError(loadingEntity);
  }

  public clearSelectedPortfolio(): void {
    this.productsStore.clearSelectedPortfolio();
  }

  public getSelectedPortfolio(portfolioId: number): void {
    this.productsStore.getSelectedPortfolio({ portfolioId });
  }

  public getPortfolioFeeConfigs(portfolioId: number): void {
    this.productsStore.getPortfolioFeeConfigs({ portfolioId });
  }

  public getModelPortfolio(portfolioId: number): void {
    this.productsStore.getModelPortfolio({ portfolioId });
  }

  public clearModelPortfolio(): void {
    this.productsStore.clearModelPortfolio();
  }

  public getModelStrategy(modelStrategyId: number): void {
    this.productsStore.getModelStrategy({ modelStrategyId });
  }

  public clearModelStrategy(): void {
    this.productsStore.clearModelStrategy();
  }

  public getModelPortfolioByRiskScore(riskScore: number): void {
    this.productsStore.getModelPortfolioByRiskScore({ riskScore });
  }

  public getPortfolioInstrumentRatios(portfolioId: number): void {
    this.productsStore.getPortfolioInstrumentRatios({ portfolioId });
  }

  public getInvestmentInstrumentRemovals(investmentId: number): void {
    this.productsStore.getInvestmentInstrumentRemovals({ investmentId });
  }

  public clearInvestmentInstrumentRemovals(): void {
    this.productsStore.clearInvestmentInstrumentRemovals();
  }

  public getPortfolioHistoryChartData(params: IPortfolioDetailChartParams, portfolioId: number): void {
    this.productsStore.getPortfolioHistoryChartData({ params, portfolioId });
  }

  public clearPortfolioHistoryChartData(): void {
    this.productsStore.clearPortfolioHistoryChartData();
  }

  public getModelPortfolioFutureDevelopmentChart(params: IModelPortfolioChartInput): void {
    this.productsStore.getModelPortfolioFutureDevelopmentChart({ params });
  }

  public getStrategyFutureDevelopmentChart(params: IModelStrategyChartInput): void {
    this.productsStore.getStrategyFutureDevelopmentChart({ params });
  }

  public getBackcastingChart(params: IBackcastingChartInput): void {
    this.productsStore.getBackcastingChart({ params });
  }

  public clearBackcastingChart(): void {
    this.productsStore.clearBackcastingChart();
  }

  public getInstrumentChart(params: IInstrumentChartInput): void {
    this.productsStore.getInstrumentChart({ params });
  }

  public clearInstrumentChart(): void {
    this.productsStore.clearInstrumentChart();
  }

  public calculateEntryFeeForNewInvestment(input: IEntryFeeCalcInput): void {
    this.productsStore.calculateEntryFeeForNewInvestment(input);
  }

  public calculateEntryFeeForExistingInvestment(investmentId: number, input: IEntryFeeCalcInput): void {
    this.productsStore.calculateEntryFeeForExistingInvestment({ investmentId, input });
  }

  public createNewPortfolio(input: INewPortfolioInput): void {
    this.productsStore.createNewPortfolio(input);
  }

  public createNewStrategy(input: INewStrategyInput): void {
    this.productsStore.createNewStrategy(input);
  }

  public updatePortfolio(input: IUpdatePortfolioV2Input, portfolioId: number): void {
    this.productsStore.updatePortfolio({ input, portfolioId });
  }

  public updateStrategy(input: IUpdateStrategyInput, portfolioId: number): void {
    this.productsStore.updateStrategy({ input, portfolioId });
  }

  public updateEfektikaStrategy(input: IUpdateEfektikaStrategyInput, portfolioId: number): void {
    this.productsStore.updateEfektikaStrategy({ input, portfolioId });
  }

  public deleteInvestmentPhoto(portfolioId: number): void {
    this.productsStore.deleteInvestmentPhoto({ portfolioId });
  }

  public clearEntryFeeToDisplay(): void {
    this.productsStore.clearEntryFeeToDisplay();
  }

  public setNewInvestmentProgress(progress: INewInvestmentProgress): void {
    this.productsStore.setNewInvestmentProgress(progress);
  }

  public updateNewInvestmentProgress(updates: Partial<INewInvestmentProgress>): void {
    this.productsStore.updateNewInvestmentProgress(updates);
  }

  public clearNewInvestmentProgress(): void {
    this.productsStore.clearNewInvestmentProgress();
  }

  public setEditInvestmentProgress(progress: IEditInvestmentProgress): void {
    this.productsStore.setEditInvestmentProgress(progress);
  }

  public updateEditInvestmentProgress(updates: Partial<IEditInvestmentProgress>): void {
    this.productsStore.updateEditInvestmentProgress(updates);
  }

  public clearEditInvestmentProgress(): void {
    this.productsStore.clearEditInvestmentProgress();
  }

  public getAvailableInstruments(params: {
    canReadEnglishOverride?: boolean,
    editedStrategyModelPortfolio?: IModelPortfolioOut,
    omitResponseFiltration?: boolean,
  } = {}): void {
    this.productsStore.getAvailableInstruments(params);
  }

  public getPublicInstruments(): void {
    this.productsStore.getPublicInstruments();
  }

  public getDraftInvestments(): void {
    this.productsStore.getDraftInvestments();
  }

  public getCashInstruments(): void {
    this.productsStore.getCashInstruments();
  }

  public getStrategyLimits(): void {
    this.productsStore.getStrategyLimits();
  }

  public getDepositInfo(portfolioId: number): void {
    this.productsStore.getDepositInfo({portfolioId});
  }

  public clearDepositInfo(): void {
    this.productsStore.clearDepositInfo();
  }

  public clearDraftInvestments(): void {
    this.productsStore.clearDraftInvestments();
  }

  public withdrawAll(portfolioId: number, clientBankAccountId: number): void {
    this.productsStore.withdrawAll({ portfolioId, clientBankAccountId });
  }

  public withdrawInvestment(portfolioId: number, clientBankAccountId: number, amount: number): void {
    this.productsStore.withdrawInvestment({ portfolioId, clientBankAccountId, amount });
  }

  //!-------------- DASHBOARD ---------------------------------------------------------
  // State signals
  public businessDays = this.dashboardStore.businessDays;
  public dashboardSummary = this.dashboardStore.summary;
  public dashboardSummaryChart = this.dashboardStore.summaryChart;

  public getBusinessDaysInRange(from: dayjs.Dayjs, to: dayjs.Dayjs): void {
    this.dashboardStore.getBusinessDaysInRange({ from, to });
  }

  public getDashboardSummary(): void {
    this.dashboardStore.getDashboardSummary();
  }

  public dashboardSummaryChartLoading(): boolean {
    return this.dashboardStore.isLoading(ELoadingEntity.DASHBOARD_CHART);
  }

  public getDashboardSummaryChart(params: IDashboardChartInput): void {
    this.dashboardStore.getDashboardSummaryChart(params);
  }

  //!-------------- FAQ ---------------------------------------------------------------
  // State signals
  public pageFaqs = this.faqStore.pageFaqs;
  public faqCategories = this.faqStore.categories;
  public faqCategoryDetail = this.faqStore.categoryDetail;
  public faqSearchResults = this.faqStore.searchResults;
  public faqLoadingProps = this.faqStore.loadingProps;

  public faqIsLoading(loadingEntity: ELoadingEntity): boolean {
    return this.faqStore.isLoading(loadingEntity);
  }

  public faqHasErrored(loadingEntity: ELoadingEntity): boolean {
    return this.faqStore.hasErrored(loadingEntity);
  }

  public faqGetError(loadingEntity: ELoadingEntity): IErrorProperty | null {
    return this.faqStore.getError(loadingEntity);
  }

  public getDashboardFaqs(): void {
    this.faqStore.getPageFaqs({ pageCode: EFaqPageCode.DASHBOARD });
  }

  public getThematicStrategyLandingFaqs(): void {
    this.faqStore.getPageFaqs({ pageCode: EFaqPageCode.LANDING_PAGE_THEMATIC_STRATEGY });
  }

  public getCustomStrategyLandingFaqs(): void {
    this.faqStore.getPageFaqs({ pageCode: EFaqPageCode.LANDING_PAGE_CUSTOM_STRATEGY });
  }

  public getFaqOverviewFaqs(): void {
    this.faqStore.getPageFaqs({ pageCode: EFaqPageCode.TOP_5 });
  }

  public getLoginFaqs(): void {
    this.faqStore.getPageFaqs({ pageCode: EFaqPageCode.LOGIN_PAGE });
  }

  public getMoneyAndTransactionsFaqs(): void {
    this.faqStore.getPageFaqs({ pageCode: EFaqPageCode.MONEY_AND_TRANSACTIONS });
  }

  public getFaqCategories(): void {
    this.faqStore.getCategories();
  }

  public getFaqCategoryDetail(categoryId: number): void {
    this.faqStore.getCategoryDetail({ categoryId });
  }

  public searchFaqs(query: string): void {
    this.faqStore.searchFaqs({ query });
  }

  public resetFaqState(): void {
    this.faqStore.resetState();
  }

  //!-------------- TRANSACTIONS ---------------------------------------------------------
  // State signals
  public transactions = this.transactionsStore.transactions;
  public transactionsTotalCount = this.transactionsStore.totalCount;
  public transactionsForSearchCount = this.transactionsStore.transactionsForSearchCount;
  public transactionsLoadingProps = this.transactionsStore.loadingProps;
  public selectedTransaction = this.transactionsStore.selectedTransaction;

  public transactionsIsLoading(loadingEntity: ELoadingEntity): boolean {
    return this.transactionsStore.isLoading(loadingEntity);
  }

  public transactionsGetError(loadingEntity: ELoadingEntity): IErrorProperty | null {
    return this.transactionsStore.getError(loadingEntity);
  }

  public getTransactions(queryParams?: ITransactionsQueryParams): void {
    this.transactionsStore.getTransactions(queryParams || {});
  }

  public getTransactionsForSearch(queryParams?: ITransactionsQueryParams): void {
    this.transactionsStore.getTransactionsForSearch({...queryParams, size: 1});
  }

  public clearSelectedTransaction(): void {
    this.transactionsStore.clearSelectedTransaction();
  }

  public setSelectedTransaction(transaction: ITransaction): void {
    this.transactionsStore.setSelectedTransaction(transaction);
  }

  //!-------------- ACCOUNT -----------------------------------------------------------
  // State signals
  public maxImageSizeBytes = this.accountStore.maxImageSizeBytes;
  public userProfilePhoto = this.accountStore.userProfilePhoto;
  public clientAdditions = this.accountStore.clientAdditions;

  // Computed signals
  public userProfilePhotoUpdateError = this.accountStore.userProfilePhotoUpdateError;

  public getImageMaxSize(): void {
    this.accountStore.getImageMaxSize();
  }

  public getUserProfilePhoto(): void {
    this.accountStore.getUserProfilePhoto();
  }

  public updateUserProfilePhoto(imageProps: IImageUploadProps, image: Blob): void {
    this.accountStore.updateUserProfilePhoto({ imageProps, image });
  }

  public deleteUserProfilePhoto(): void {
    this.accountStore.deleteUserProfilePhoto();
  }

  public getThematicStrategiesRotation(limit: number): void {
    this.productsStore.getThematicStrategies({ rotation: true, limit });
  }

  public getThematicStrategies(): void {
    this.productsStore.getThematicStrategies({ rotation: false });
  }

  public clearThematicStrategies(): void {
    this.productsStore.clearThematicStrategies();
  }

  public isClientAdditionsLoading(): boolean {
    return this.accountStore.isLoading(ELoadingEntity.CLIENT_ADDITIONS);
  }

  public getClientAdditions(): void {
    this.accountStore.getClientAdditions();
  }

  //!-------------- BLOG ---------------------------------------------------------------
  // State signals
  public blogArticlesPage = this.blogStore.articlesPage;
  public blogLoadingProps = this.blogStore.loadingProps;
  public articleDetail = this.blogStore.articleDetail;

  public blogIsLoading(loadingEntity: ELoadingEntity): boolean {
    return this.blogStore.isLoading(loadingEntity);
  }

  public getBlogArticlesPage(pageRequest: IPageRequest, search: string | null): void {
    this.blogStore.getArticlesPage({ pageRequest, search });
  }

  public getBlogArticleDetail(articleId: number): void {
    this.blogStore.getArticleDetail({ articleId });
  }

  public getBlogArticlePreview(token: string): void {
    this.blogStore.getArticlePreview({ token });
  }

  public resetBlogState(): void {
    this.blogStore.resetState();
  }

  //!-------------- BANK ACCOUNTS ------------------------------------------------------
  public bankAccounts = this.bankAccountsStore.bankAccounts;

  public bankAccountsIsLoading(loadingEntity: ELoadingEntity): boolean {
    return this.bankAccountsStore.isLoading(loadingEntity);
  }

  public bankAccountsHasErrored(loadingEntity: ELoadingEntity): boolean {
    return this.bankAccountsStore.hasErrored(loadingEntity);
  }

  public getBankAccounts(portfolioId: number): void {
    this.bankAccountsStore.getBankAccounts({ portfolioId });
  }

}
